import type { TicketStatusLabels } from './ticket-status'

// API base URL is the same for all tenants (set at build/deploy time)
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://assistengine.ai'

const AZURE_TENANT_STORAGE_KEY = 'ae_teams_azure_tenant_id'
const SSO_TOKEN_STORAGE_KEY = 'ae_teams_sso_token'

export function setAzureTenantId(tenantId: string) {
  localStorage.setItem(AZURE_TENANT_STORAGE_KEY, tenantId)
}

export function getAzureTenantId(): string {
  return localStorage.getItem(AZURE_TENANT_STORAGE_KEY) || ''
}

export function setSsoToken(token: string) {
  localStorage.setItem(SSO_TOKEN_STORAGE_KEY, token)
}

export function getSsoToken(): string {
  return localStorage.getItem(SSO_TOKEN_STORAGE_KEY) || ''
}

export function clearSsoToken() {
  localStorage.removeItem(SSO_TOKEN_STORAGE_KEY)
}

function getUserInfo(): { email: string; name: string; id: string } {
  try {
    const stored = localStorage.getItem('teams_user_info')
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    console.warn('Failed to parse user info', e)
  }

  if (import.meta.env.DEV) {
    return {
      email: 'user@example.com',
      name: 'Test User',
      id: 'test-user-id',
    }
  }

  return { email: '', name: '', id: '' }
}

export function setUserInfo(email: string, name: string, id: string) {
  localStorage.setItem('teams_user_info', JSON.stringify({ email, name, id }))
}

function buildTeamsHeaders(
  extra?: HeadersInit,
  includeJsonContentType: boolean = true
): HeadersInit {
  const azureTenantId = getAzureTenantId()
  const ssoToken = getSsoToken()
  const userInfo = getUserInfo()

  const headers: HeadersInit = {
    ...(includeJsonContentType ? { 'Content-Type': 'application/json' } : {}),
    'X-Teams-User-Email': userInfo.email,
    'X-Teams-User-Name': userInfo.name,
    'X-Teams-User-Id': userInfo.id,
    ...(ssoToken ? { 'X-Teams-SSO-Token': ssoToken } : {}),
    ...(azureTenantId ? { 'X-Teams-Azure-Tenant-Id': azureTenantId } : {}),
    ...extra,
  }

  return headers
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const azureTenantId = getAzureTenantId()
  const ssoToken = getSsoToken()

  if (!API_BASE_URL) throw new Error('API Base URL is not configured.')
  if (!ssoToken && !azureTenantId) {
    throw new Error('Not connected. Your Teams admin needs to configure this integration.')
  }

  try {
    const response = await fetch(`${API_BASE_URL}/api/teams${endpoint}`, {
      ...options,
      headers: buildTeamsHeaders(options.headers),
    })

    if (!response.ok) {
      const errorText = await response.text().catch(() => 'Unknown error')
      let errorMessage = `HTTP ${response.status}`

      try {
        const errorJson = JSON.parse(errorText)
        errorMessage = errorJson.error || errorJson.message || errorMessage
      } catch {
        if (errorText.length < 300) errorMessage = errorText
      }

      throw new Error(errorMessage)
    }

    return response.json()
  } catch (error) {
    const msg = error instanceof Error ? error.message : String(error)
    throw new Error(msg)
  }
}

export const api = {
  async validateAuth(): Promise<{
    success: boolean
    integration: any
    tenant: any
    priorities: any[]
  }> {
    return request('/auth', { method: 'POST' })
  },

  async getConfig(): Promise<AppConfigResponse> {
    return request('/config')
  },

  async getTickets(params?: {
    status?: string
    ticketType?: string
    limit?: number
    offset?: number
  }): Promise<{
    tickets: Ticket[]
    pagination: { total: number; limit: number; offset: number; hasMore: boolean }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.ticketType) searchParams.set('ticket_type', params.ticketType)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())

    const query = searchParams.toString()
    return request(`/tickets${query ? `?${query}` : ''}`)
  },

  async getTicket(
    id: string
  ): Promise<{ ticket: TicketDetail; messages: Message[]; attachments: Attachment[] }> {
    return request(`/tickets/${id}`)
  },

  async createTicket(
    data: CreateTicketData
  ): Promise<{ success: boolean; ticket: Ticket; message: string }> {
    return request('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async addTicketMessage(
    ticketId: string,
    data: { content: string; attachmentIds?: string[] }
  ): Promise<{ success: boolean; message: Message }> {
    return request(`/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getQueueStatus(): Promise<QueueStatus> {
    return request('/queue')
  },

  async getAdminOverview(): Promise<AdminOverviewResponse> {
    return request('/admin/overview')
  },

  async getAdminTickets(params?: {
    status?: string
    ticketType?: string
    search?: string
    limit?: number
    offset?: number
  }): Promise<{
    tickets: AdminTicket[]
    pagination: { total: number; limit: number; offset: number; hasMore: boolean }
  }> {
    const searchParams = new URLSearchParams()
    if (params?.status) searchParams.set('status', params.status)
    if (params?.ticketType) searchParams.set('ticket_type', params.ticketType)
    if (params?.search) searchParams.set('search', params.search)
    if (params?.limit) searchParams.set('limit', params.limit.toString())
    if (params?.offset) searchParams.set('offset', params.offset.toString())

    const query = searchParams.toString()
    return request(`/admin/tickets${query ? `?${query}` : ''}`)
  },

  async getAdminTicket(
    id: string
  ): Promise<{ ticket: AdminTicketDetail; messages: AdminMessage[] }> {
    return request(`/admin/tickets/${id}`)
  },

  async addAdminTicketMessage(
    ticketId: string,
    data: { content: string; attachmentIds?: string[] }
  ): Promise<{ success: boolean; message: AdminMessage }> {
    return request(`/admin/tickets/${ticketId}/messages`, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateAdminTicketStatus(
    ticketId: string,
    data: { status?: string; priority?: string }
  ): Promise<{
    success: boolean
    ticket: {
      id: string
      ticketNumber: string
      status: string
      statusLabel: string
      priority: string
      updatedAt: string
    }
  }> {
    return request(`/admin/tickets/${ticketId}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  },

  async uploadFile(file: File): Promise<{ success: boolean; attachment: Attachment }> {
    const formData = new FormData()
    formData.append('file', file)

    const response = await fetch(`${API_BASE_URL}/api/teams/upload`, {
      method: 'POST',
      headers: buildTeamsHeaders({}, false),
      body: formData,
    })

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Upload failed' }))
      throw new Error(error.error || `HTTP ${response.status}`)
    }

    return response.json()
  },

  async getUploadConfig(): Promise<{
    allowedTypes: string[]
    maxFileSize: number
    maxFileSizeMB: number
  }> {
    return request('/upload')
  },
}

export type TicketType = 'support' | 'it_request'

export interface Ticket {
  id: string
  ticketNumber: string
  subject: string
  status: string
  statusLabel: string
  priority: string
  priorityScore?: number
  ticketType: TicketType
  createdAt: string
  updatedAt: string
  clientName?: string
  messageCount?: number
  lastMessage?: string
}

export interface TicketDetail extends Ticket {
  originalBody: string
  tags: string[]
  categoryName?: string
  subcategoryName?: string
  businessJustification?: string
  managerEmail?: string
  managerName?: string
  requestItems?: string
}

export interface CreateTicketData {
  subject: string
  message: string
  priority?: string
  categoryId?: string
  subcategoryId?: string
  attachmentIds?: string[]
  ticketType?: TicketType
  businessJustification?: string
  managerEmail?: string
  managerName?: string
  requestItems?: string
}

export interface Message {
  id: string
  senderType: 'client' | 'agent' | 'system'
  content: string
  createdAt: string
  attachments?: Attachment[]
}

export interface Attachment {
  id: string
  filename: string
  fileSize: number
  contentType: string
  createdAt: string
}

export interface QueueStatus {
  enabled: boolean
  companyActiveTickets?: number
  aheadInQueue?: number
  queueLevel?: 'low' | 'normal' | 'busy' | 'very_busy'
  avgResponseTime?: string
  avgResponseMinutes?: number | null
  statusMessage?: string
  message?: string
}

export interface CategoryOption {
  id: string
  name: string
  subcategories: SubcategoryOption[]
}

export interface SubcategoryOption {
  id: string
  name: string
}

export interface PriorityOption {
  label: string
  value: string
  color: string
  description?: string
}

export interface TeamsAccess {
  role: 'requester' | 'client_admin'
  isClientAdmin: boolean
  canManageClientTickets: boolean
  canUpdateTicketStatus: boolean
  canCommentOnClientTickets: boolean
  canViewInternalNotes: boolean
  clientId?: string | null
}

export interface AppConfigResponse {
  integration: {
    id: string
    name: string
    welcomeMessage: string
    allowPrioritySelection: boolean
    defaultPriority: string
    showQueueCount: boolean
    accentColor: string
  }
  tenant: {
    name: string
    slug?: string
    logoUrl?: string
  }
  access: TeamsAccess
  statusLabels: TicketStatusLabels
  priorities: PriorityOption[]
  categories: CategoryOption[]
  fileUpload: {
    enabled: boolean
    maxSizeMB: number
    allowedTypes: string[]
  }
}

export interface AdminOverviewResponse {
  client: {
    id: string
    name: string
    domain: string
    contact_email: string
  }
  summary: {
    totalTickets: number
    activeTickets: number
    resolvedTickets: number
    closedTickets: number
    avgFirstResponseMinutes: number | null
  }
  statusBreakdown: {
    new: number
    open: number
    pending: number
    resolved: number
    closed: number
    [key: string]: number
  }
  recent: AdminTicket[]
}

export interface AdminTicket {
  id: string
  ticketNumber: string
  subject: string
  status: string
  statusLabel: string
  priority: string
  priorityScore?: number
  ticketType: TicketType
  createdAt: string
  updatedAt: string
  senderName?: string
  senderEmail?: string
  assignedAgentName?: string
  messageCount?: number
  lastMessage?: string
}

export interface AdminTicketDetail extends AdminTicket {
  originalBody: string
  tags: string[]
  categoryName?: string
  subcategoryName?: string
  businessJustification?: string
  managerEmail?: string
  managerName?: string
  requestItems?: string
}

export interface AdminMessage {
  id: string
  senderType: 'client' | 'agent' | 'system'
  content: string
  isInternal?: boolean
  createdAt: string
  attachments?: Attachment[]
}
